import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Instagram, Linkedin } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function Contacto() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Debes aceptar la Política de Privacidad para enviar el mensaje");
    setLoading(true);
    try {
      await api.post("/contact", { ...form, consent });
      toast.success("Mensaje enviado. Nos pondremos en contacto contigo.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al enviar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-16">
      <div className="md:col-span-5">
        <div className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] mb-6">Contacto</div>
        <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-8">
          Cuéntanos <em className="gold-text not-italic">tu caso</em>.
        </h1>
        <p className="text-zinc-400 mb-10 leading-relaxed">
          Estamos aquí para ayudarte. Envíanos un mensaje y te responderemos lo antes posible.
        </p>
        <div className="space-y-5">
          <a href="tel:+34689183582" className="flex items-center gap-4 text-zinc-300 hover:text-[#D4AF37] transition-colors" data-testid="contact-phone">
            <div className="w-10 h-10 rounded-full border border-[#D4AF37]/30 grid place-items-center">
              <Phone className="w-4 h-4 text-[#D4AF37]" />
            </div>
            689 183 582
          </a>
          <a href="mailto:info@anvasesores.es" className="flex items-center gap-4 text-zinc-300 hover:text-[#D4AF37] transition-colors" data-testid="contact-email">
            <div className="w-10 h-10 rounded-full border border-[#D4AF37]/30 grid place-items-center">
              <Mail className="w-4 h-4 text-[#D4AF37]" />
            </div>
            info@anvasesores.es
          </a>
          <a href="https://api.whatsapp.com/send?phone=34689183582" target="_blank" rel="noreferrer" className="flex items-center gap-4 text-zinc-300 hover:text-[#D4AF37] transition-colors">
            <div className="w-10 h-10 rounded-full border border-[#D4AF37]/30 grid place-items-center">
              <MessageCircle className="w-4 h-4 text-[#D4AF37]" />
            </div>
            WhatsApp
          </a>
          <a href="https://instagram.com/asesoresanv/" target="_blank" rel="noreferrer" className="flex items-center gap-4 text-zinc-300 hover:text-[#D4AF37] transition-colors">
            <div className="w-10 h-10 rounded-full border border-[#D4AF37]/30 grid place-items-center">
              <Instagram className="w-4 h-4 text-[#D4AF37]" />
            </div>
            @asesoresanv
          </a>
          <a href="https://www.linkedin.com/company/acta-non-verba-s-l/?viewAsMember=true" target="_blank" rel="noreferrer" className="flex items-center gap-4 text-zinc-300 hover:text-[#D4AF37] transition-colors" data-testid="contact-linkedin">
            <div className="w-10 h-10 rounded-full border border-[#D4AF37]/30 grid place-items-center">
              <Linkedin className="w-4 h-4 text-[#D4AF37]" />
            </div>
            LinkedIn · Acta Non Verba, S.L.
          </a>
          <div className="flex items-center gap-4 text-zinc-300">
            <div className="w-10 h-10 rounded-full border border-[#D4AF37]/30 grid place-items-center">
              <MapPin className="w-4 h-4 text-[#D4AF37]" />
            </div>
            Cataluña · Garraf, Baix Camp y más
          </div>
        </div>
      </div>

      <form
        onSubmit={submit}
        className="md:col-span-7 bg-[#121214] border border-white/5 rounded-2xl p-8 md:p-10 space-y-6"
        data-testid="contact-form"
      >
        <div>
          <label className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-2 block">Nombre</label>
          <input
            required
            data-testid="contact-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-2 block">Email</label>
            <input
              required
              type="email"
              data-testid="contact-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-2 block">Teléfono</label>
            <input
              data-testid="contact-phone-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
            />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-2 block">Mensaje</label>
          <textarea
            required
            rows={6}
            data-testid="contact-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"
          />
        </div>
        <label className="flex items-start gap-3 text-sm text-zinc-400 leading-relaxed cursor-pointer">
          <input
            type="checkbox"
            required
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="contact-consent"
            className="mt-1 w-4 h-4 accent-[#D4AF37] flex-shrink-0 cursor-pointer"
          />
          <span>
            He leído y acepto la{" "}
            <Link to="/privacidad" target="_blank" className="text-[#D4AF37] hover:underline">Política de Privacidad</Link>. Consiento el tratamiento de mis datos para atender mi consulta.
          </span>
        </label>
        <button
          type="submit"
          disabled={loading}
          data-testid="contact-submit"
          className="px-8 py-3.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors disabled:opacity-60"
        >
          {loading ? "Enviando…" : "Enviar mensaje"}
        </button>
      </form>
    </div>
  );
}
